import { createContext, useContext, Fragment, type ReactNode } from "react";
import { Check } from "lucide-react";
import { Button } from "@hipotecar/ui/button";
import { Card, CardContent } from "@hipotecar/ui/card";
import { subscriptionCardVariants } from "../../styles/variants";
import { cn } from "@hipotecar/ui/lib/utils";
import type { PricingFeature } from "../../schemas/ui";

// Context for sharing state across compound components
interface PricingCardContextValue {
	variant: "default" | "highlighted";
	isCurrentPlan: boolean;
}

const PricingCardContext = createContext<PricingCardContextValue | undefined>(
	undefined,
);

const usePricingCardContext = () => {
	const context = useContext(PricingCardContext);
	if (!context) {
		throw new Error(
			"PricingCard compound components must be used within PricingCard",
		);
	}
	return context;
};

/**
 * Feature copy carries `**bold**` spans so a line can lead with the figure it
 * sells and trail off into the explanation, the way the design words it.
 * Hoisted because a global RegExp is stateful and rebuilding it per render is
 * wasted work.
 */
const EMPHASIS_PATTERN = /\*\*(.+?)\*\*/g;

function renderEmphasis(text: string): ReactNode {
	EMPHASIS_PATTERN.lastIndex = 0;

	const parts: ReactNode[] = [];
	let cursor = 0;
	let match = EMPHASIS_PATTERN.exec(text);

	while (match !== null) {
		if (match.index > cursor) {
			parts.push(text.slice(cursor, match.index));
		}
		parts.push(
			<strong
				key={match.index}
				className="font-semibold text-gray-900 dark:text-white"
			>
				{match[1]}
			</strong>,
		);
		cursor = match.index + match[0].length;
		match = EMPHASIS_PATTERN.exec(text);
	}

	if (cursor === 0) return text;
	if (cursor < text.length) parts.push(text.slice(cursor));

	return parts.map((part, index) => <Fragment key={index}>{part}</Fragment>);
}

// Root Component
interface PricingCardProps {
	children: ReactNode;
	variant?: "default" | "highlighted";
	isCurrentPlan?: boolean;
	/** Copy for the pill that straddles the top border, e.g. "El más elegido" */
	ribbon?: string;
	className?: string;
}

function PricingCard({
	children,
	variant = "default",
	isCurrentPlan = false,
	ribbon,
	className,
}: PricingCardProps) {
	return (
		<PricingCardContext.Provider value={{ variant, isCurrentPlan }}>
			{/* The ribbon overhangs the card, so it sits in a wrapper that the
			    card's rounded clipping cannot reach. pt-3 on every card keeps
			    the three tops on one line whether or not a ribbon is present. */}
			<div className="relative h-full pt-3">
				{ribbon && (
					<span className="absolute top-0 left-1/2 z-10 -translate-x-1/2 rounded-full bg-brand-500 px-4 py-1.5 text-[11px] font-bold tracking-wide whitespace-nowrap text-gray-900 uppercase shadow-theme-md">
						{ribbon}
					</span>
				)}
				<Card
					className={cn(
						subscriptionCardVariants({ variant }),
						"h-full",
						className,
					)}
				>
					{children}
				</Card>
			</div>
		</PricingCardContext.Provider>
	);
}

// Plan identity: tier name over the one-line promise
interface PlanProps {
	name: string;
	tagline?: string | null;
}

function Plan({ name, tagline }: PlanProps) {
	const { variant } = usePricingCardContext();

	return (
		<div className="mb-5">
			<div
				className={cn(
					"text-xs font-bold tracking-[0.12em] uppercase",
					variant === "highlighted"
						? "text-brand-500 dark:text-brand-400"
						: "text-gray-500 dark:text-gray-400",
				)}
			>
				{name}
			</div>
			{tagline && (
				<p className="mt-2 text-lg/snug text-gray-500 dark:text-gray-400">
					{tagline}
				</p>
			)}
		</div>
	);
}

// Price Sub-component
interface PriceProps {
	value: string | number;
	currency?: string;
	period?: string;
	/** Badge next to the period, e.g. "facturado anual" */
	note?: string | null;
	/** Secondary line under the price, e.g. the real yearly charge */
	caption?: string | null;
}

function Price({
	value,
	currency = "S/",
	period = "/ mes",
	note,
	caption,
}: PriceProps) {
	return (
		<div className="mb-6">
			<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-gray-900 dark:text-white">
				<span className="text-[2.75rem] leading-none font-bold tracking-tight">
					{currency} {value}
				</span>
				<span className="text-sm text-gray-500 dark:text-gray-400">
					{period}
				</span>
				{note && (
					<span className="rounded-md bg-brand-500/15 px-1.5 py-0.5 text-xs font-medium whitespace-nowrap text-brand-600 dark:text-brand-400">
						{note}
					</span>
				)}
			</div>
			{caption && (
				<div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
					{caption}
				</div>
			)}
		</div>
	);
}

// Features Sub-component
interface FeaturesProps {
	items: PricingFeature[];
}

function Features({ items }: FeaturesProps) {
	const { variant } = usePricingCardContext();
	const isHighlighted = variant === "highlighted";

	return (
		<ul className="flex-1 space-y-4">
			{items.map((feature, index) => (
				<li key={index} className="flex items-start gap-3">
					<Check
						aria-hidden="true"
						className={cn(
							"mt-0.5 size-4 shrink-0",
							feature.included
								? isHighlighted
									? "text-brand-500 dark:text-brand-400"
									: "text-gray-400 dark:text-gray-500"
								: "text-gray-300 dark:text-gray-700",
						)}
					/>
					<span
						className={cn(
							"text-sm/relaxed",
							feature.included
								? "text-gray-600 dark:text-gray-400"
								: "text-gray-400 line-through dark:text-gray-600",
						)}
					>
						{renderEmphasis(feature.text)}
					</span>
				</li>
			))}
		</ul>
	);
}

// CTA Sub-component
interface CTAProps {
	children: ReactNode;
	onClick?: () => void;
	disabled?: boolean;
}

function CTA({ children, onClick, disabled = false }: CTAProps) {
	const { variant, isCurrentPlan } = usePricingCardContext();

	return (
		<Button
			variant="ghost"
			onClick={onClick}
			disabled={disabled || isCurrentPlan}
			className={cn(
				"mb-7 h-12 w-full rounded-xl text-[15px] font-semibold transition-transform active:scale-[0.98]",
				variant === "highlighted"
					? "bg-brand-500 text-gray-900 hover:bg-brand-600 dark:bg-brand-500 dark:text-gray-900 dark:hover:bg-brand-600"
					: "border border-gray-300 bg-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900 dark:border-white/15 dark:bg-transparent dark:text-white dark:hover:bg-white/5 dark:hover:text-white",
			)}
		>
			{children}
		</Button>
	);
}

// Content wrapper for padding
interface ContentProps {
	children: ReactNode;
}

function Content({ children }: ContentProps) {
	return (
		<CardContent className="flex h-full flex-col p-7 sm:p-8">
			{children}
		</CardContent>
	);
}

// Attach sub-components to root
PricingCard.Plan = Plan;
PricingCard.Price = Price;
PricingCard.Features = Features;
PricingCard.CTA = CTA;
PricingCard.Content = Content;

export default PricingCard;

// Also export the types for convenience
export type {
	PricingCardProps,
	PlanProps,
	PriceProps,
	FeaturesProps,
	CTAProps,
};
